import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
  type TouchEvent,
} from 'react';
import type { Language, VisitingCardPayload, VisitingCardProduct } from '../../types';
import { _t, languageDirection, pickI18nText } from '../../lib/i18n';
import { api } from '../../lib/api';
import { visitingCardCatalogKindsInOrder, effectiveVisitingCardPhotoLink } from '../../lib/visitingCardConfig.js';
import { tenantCatalogProductUrl, tenantCatalogUrl } from '../../lib/tenantHost';
import { useFingerFollowSwipe } from '../../hooks/useFingerFollowSwipe';
import { VisitingCardContactButtons } from './VisitingCardContactButtons';
import { VisitingCardNewsProvider } from './VisitingCardNews';
import { VisitingCardA11yFooter } from './VisitingCardA11yFooter';
import { PublishedReviews } from '../gallery/publishedReviews';
import { SafeMarkdownContent } from '../common/SafeMarkdownContent';
import { splitNoteParagraphs } from '../../lib/noteBlog';

type VisitingCardCanvasProps = {
  lang: Language;
  card: VisitingCardPayload;
};

type VisitingCardCanvasRootProps = VisitingCardCanvasProps & {
  /** Lets the page hide its own chrome (language chips) while the viewer is open. */
  onPreviewOpenChange?: (open: boolean) => void;
};

type PhotoAction =
  | { kind: 'href'; href: string; external: boolean }
  | { kind: 'preview' };

/** One slide of the full-screen viewer. */
type PreviewItem = {
  id: string;
  /** Slides of the same work share this — used to open the viewer on the tapped work. */
  productId: string;
  imageUrl: string;
  title: string;
  description: string;
  /** Absent when the work has no place on the storefront to open. */
  storefrontHref: string | null;
};

/** Set by the canvas root; sections use it so the viewer state lives in one place. */
type PreviewApi = {
  open: (items: PreviewItem[], index: number) => void;
  /** Works: full catalog filmstrip (fetched once); fallback keeps the card slice if the request fails. */
  openCatalogAt: (productId: string, fallback: PreviewItem[]) => void;
};

const PreviewContext = createContext<PreviewApi | null>(null);

function storefrontHrefFor(
  card: VisitingCardPayload,
  lang: Language,
  product: VisitingCardProduct,
): string | null {
  const slug = card.tenant.slug;
  if (!slug) return null;
  return product.listedInCatalog
    ? tenantCatalogProductUrl(slug, product.id, lang)
    : tenantCatalogUrl(slug, lang);
}

function previewItemsFrom(
  card: VisitingCardPayload,
  lang: Language,
  products: VisitingCardProduct[],
): PreviewItem[] {
  // One slide per photo: swiping runs through a work's gallery, then on to the next work.
  return products.flatMap((product) => {
    const urls = product.imageUrls?.length
      ? product.imageUrls
      : [product.imageUrl].filter(Boolean).map(String);
    return urls.map((url, i) => ({
      id: `${product.id}:${i}`,
      productId: product.id,
      imageUrl: String(url),
      title: product.name,
      description: String(product.description || '').trim(),
      // Services keep their external link in the viewer; works omit storefront when card-only.
      storefrontHref: String(product.externalUrl || '').trim()
        || (card.config.cardOnly ? null : storefrontHrefFor(card, lang, product)),
    }));
  });
}

/**
 * Services and works both honor `photoLink`.
 * Services with an external URL: preview → fullscreen; other modes → that URL (or none).
 */
function buildPhotoAction(
  card: VisitingCardPayload,
  lang: Language,
  product: VisitingCardProduct,
): PhotoAction | null {
  const albumHref = String(product.albumHref || '').trim();
  if (product.kind === 'service_note') {
    return product.imageUrl ? { kind: 'preview' } : null;
  }
  if (albumHref) {
    if (card.config.worksTapPreview === false) return null;
    return { kind: 'href', href: albumHref, external: false };
  }
  const mode = effectiveVisitingCardPhotoLink(card.config);
  const externalUrl = String(product.externalUrl || '').trim();
  if (externalUrl) {
    if (mode === 'none') return null;
    if (mode === 'preview') {
      return product.imageUrl ? { kind: 'preview' } : { kind: 'href', href: externalUrl, external: true };
    }
    return { kind: 'href', href: externalUrl, external: true };
  }
  const slug = card.tenant.slug;
  if (mode === 'none' || !slug) {
    return null;
  }
  if (mode === 'preview') {
    return product.imageUrl ? { kind: 'preview' } : null;
  }
  if (mode === 'product' && product.listedInCatalog) {
    return { kind: 'href', href: tenantCatalogProductUrl(slug, product.id, lang), external: false };
  }
  return { kind: 'href', href: tenantCatalogUrl(slug, lang), external: false };
}

/**
 * Wrap media in a tap target without swallowing flip-card gestures on the surrounding text.
 * A link stays a link so long-press and "open in new tab" keep working.
 */
function PhotoTapFrame({
  action,
  onPreview,
  label,
  className,
  children,
}: {
  action: PhotoAction | null;
  onPreview?: () => void;
  label: string;
  className: string;
  children: ReactNode;
}) {
  if (!action) {
    return <div className={`overflow-hidden ${className}`}>{children}</div>;
  }

  const stop = {
    onPointerDown: (e: { stopPropagation: () => void }) => e.stopPropagation(),
    onTouchEnd: (e: { stopPropagation: () => void }) => e.stopPropagation(),
  };

  if (action.kind === 'preview') {
    return (
      <button
        type="button"
        data-no-flip
        aria-label={label}
        className={`relative block w-full overflow-hidden ${className}`}
        onClick={(e) => {
          e.stopPropagation();
          onPreview?.();
        }}
        {...stop}
      >
        {children}
      </button>
    );
  }

  return (
    <a
      href={action.href}
      {...(action.external ? { target: '_blank', rel: 'noreferrer' } : {})}
      data-no-flip
      aria-label={label}
      className={`relative block overflow-hidden ${className}`}
      onClick={(e) => e.stopPropagation()}
      {...stop}
    >
      {children}
    </a>
  );
}

const MAX_PREVIEW_ZOOM = 4;

/**
 * Double tap zooms to the photo's own pixel size (capped), anchored on the tapped point;
 * while zoomed a one-finger drag pans. Transforms are written straight to the node so a
 * drag never re-renders the viewer.
 */
function PreviewPhoto({
  item,
  zoomed,
  onToggleZoom,
}: {
  item: PreviewItem;
  zoomed: boolean;
  onToggleZoom: (next: boolean, point?: { x: number; y: number }) => void;
}) {
  const imgRef = useRef<HTMLImageElement>(null);
  const stateRef = useRef({ scale: 1, x: 0, y: 0 });
  const panRef = useRef<{ x: number; y: number } | null>(null);
  const lastTapRef = useRef(0);
  /** iOS emits a synthetic dblclick after a double tap — it must not toggle zoom back. */
  const handledAtRef = useRef(0);

  const paint = (animate: boolean) => {
    const img = imgRef.current;
    if (!img) return;
    const { scale, x, y } = stateRef.current;
    img.style.transition = animate ? 'transform 200ms ease-out' : 'none';
    img.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${scale})`;
  };

  useEffect(() => {
    if (zoomed) return;
    stateRef.current = { scale: 1, x: 0, y: 0 };
    paint(true);
  }, [zoomed, item.id]);

  /** Keep the tapped point under the finger when scaling up. */
  const zoomTo = (point: { x: number; y: number }) => {
    const img = imgRef.current;
    if (!img) return;
    const rect = img.getBoundingClientRect();
    const natural = img.naturalWidth || rect.width;
    const scale = Math.min(MAX_PREVIEW_ZOOM, Math.max(2, natural / Math.max(rect.width, 1)));
    const dx = point.x - (rect.left + rect.width / 2);
    const dy = point.y - (rect.top + rect.height / 2);
    stateRef.current = { scale, x: -dx * (scale - 1), y: -dy * (scale - 1) };
    paint(true);
  };

  const onDoubleTap = (point: { x: number; y: number }) => {
    handledAtRef.current = Date.now();
    if (zoomed) {
      onToggleZoom(false);
      return;
    }
    onToggleZoom(true, point);
    zoomTo(point);
  };

  return (
    <img
      ref={imgRef}
      src={item.imageUrl}
      alt={item.title}
      className="max-h-full max-w-full origin-center object-contain will-change-transform"
      draggable={false}
      onDoubleClick={(e) => {
        if (Date.now() - handledAtRef.current < 700) return;
        onDoubleTap({ x: e.clientX, y: e.clientY });
      }}
      onTouchStart={(e) => {
        const touch = e.touches[0];
        if (!touch) return;
        const now = Date.now();
        if (now - lastTapRef.current < 300) {
          lastTapRef.current = 0;
          onDoubleTap({ x: touch.clientX, y: touch.clientY });
          return;
        }
        lastTapRef.current = now;
        panRef.current = zoomed ? { x: touch.clientX, y: touch.clientY } : null;
      }}
      onTouchMove={(e) => {
        const start = panRef.current;
        const touch = e.touches[0];
        if (!start || !touch || !zoomed) return;
        // Zoomed panning owns the gesture; the filmstrip swipe is disabled meanwhile.
        e.stopPropagation();
        stateRef.current = {
          ...stateRef.current,
          x: stateRef.current.x + (touch.clientX - start.x),
          y: stateRef.current.y + (touch.clientY - start.y),
        };
        panRef.current = { x: touch.clientX, y: touch.clientY };
        paint(false);
      }}
      onTouchEnd={() => {
        panRef.current = null;
      }}
    />
  );
}

/**
 * Full-screen viewer inside the card: swipe or arrows move between photos, and only an
 * explicit button leaves for the storefront. Absolute (not fixed) so the admin phone frame
 * keeps it inside the mock device.
 */
function PhotoPreview({
  lang,
  items,
  index,
  onIndex,
  onClose,
}: {
  lang: Language;
  items: PreviewItem[];
  index: number;
  onIndex: (next: number) => void;
  onClose: () => void;
}) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const item = items[index];
  const many = items.length > 1;
  const [zoomed, setZoomed] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const gesturesEnabled = !zoomed && !expanded;

  useEffect(() => {
    setZoomed(false);
    setExpanded(false);
  }, [index]);

  const go = useCallback(
    (dir: 1 | -1) => onIndex((index + dir + items.length) % items.length),
    [index, items.length, onIndex],
  );

  useFingerFollowSwipe({
    stageRef,
    dismissRef: overlayRef,
    onDismiss: onClose,
    canPrev: many,
    canNext: many,
    onPrev: () => go(-1),
    onNext: () => go(1),
    resetKey: index,
    filmstrip: true,
    // A zoomed photo pans, an open caption scrolls — neither turns the page or dismisses.
    enabled: gesturesEnabled,
  });

  // Warm the photos just outside the strip: decoding one the moment it enters the DOM costs a
  // frame, and that hitch shows up as a flicker right after the swipe settles.
  useEffect(() => {
    if (!many) return;
    // Only the ones outside the strip: ±1 are already mounted and loading on their own.
    for (const offset of [2, -2]) {
      const ahead = items[(index + offset + items.length * 2) % items.length];
      if (ahead) new Image().src = ahead.imageUrl;
    }
  }, [index, items, many]);

  // Android back button and browser back close the viewer instead of leaving the card.
  useEffect(() => {
    window.history.pushState({ visitingCardPreview: true }, '');
    const onPop = () => onClose();
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      if (window.history.state?.visitingCardPreview) {
        window.history.back();
      }
    };
  }, [onClose]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') onIndex((index + 1) % items.length);
      else if (e.key === 'ArrowLeft') onIndex((index - 1 + items.length) % items.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, items.length, onIndex, onClose]);

  if (!item) return null;

  const prevItem = many ? items[(index - 1 + items.length) % items.length] : null;
  const nextItem = many ? items[(index + 1) % items.length] : null;
  // With two photos both neighbours are the same one, so ids alone would collide as keys.
  const sameNeighbour = Boolean(prevItem && nextItem && prevItem.id === nextItem.id);

  return (
    <div
      ref={overlayRef}
      className="absolute inset-0 z-30 bg-black/95 text-white will-change-transform"
      role="dialog"
      aria-modal="true"
      data-no-flip
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
      onTouchEnd={(e) => e.stopPropagation()}
    >
      {/* Floating chrome: a header row in the flow would push the photo below the true centre. */}
      <div className="pointer-events-none absolute inset-x-0 top-0 z-20 flex items-center justify-between px-3 py-2 text-xs">
        <div className="pointer-events-auto flex min-w-0 items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            aria-label={_t('visitingCard.photoPreviewBack', lang)}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/15 text-base leading-none"
          >
            ‹
          </button>
          <span className="truncate tabular-nums text-white/70">
            {many ? `${index + 1} / ${items.length}` : ''}
          </span>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={_t('visitingCard.photoPreviewClose', lang)}
          className="pointer-events-auto inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/15 text-lg leading-none"
        >
          ×
        </button>
      </div>

      {/* Filmstrip: the pane follows the finger, so neighbours slide in from the sides. */}
      <div ref={stageRef} className="absolute inset-0 touch-none overflow-hidden">
        <div className="absolute inset-y-0" style={{ left: '-100%', width: '300%' }}>
          <div
            data-finger-follow-pane
            className="flex h-full w-full will-change-transform"
          >
            {[prevItem, item, nextItem].map((slide, i) => (
              <div
                // Keyed by photo, not by slot: after a turn React moves the same <img> into the
                // centre instead of remounting it, which used to flash over the settled photo.
                key={slide ? (sameNeighbour ? `${slide.id}-${i}` : slide.id) : `empty-${i}`}
                className="flex h-full w-1/3 shrink-0 items-center justify-center px-3"
              >
                {slide ? (
                  <PreviewPhoto
                    item={slide}
                    zoomed={slide === item && zoomed}
                    onToggleZoom={
                      slide === item ? (next) => setZoomed(next) : () => undefined
                    }
                  />
                ) : null}
              </div>
            ))}
          </div>
        </div>
        {many ? (
          <>
            <button
              type="button"
              onClick={() => go(-1)}
              aria-label={_t('visitingCard.photoPreviewPrev', lang)}
              className="absolute left-1 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/15 px-2 py-3 text-sm"
            >
              ‹
            </button>
            <button
              type="button"
              onClick={() => go(1)}
              aria-label={_t('visitingCard.photoPreviewNext', lang)}
              className="absolute right-1 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/15 px-2 py-3 text-sm"
            >
              ›
            </button>
          </>
        ) : null}

        <PreviewCaption
          lang={lang}
          item={item}
          expanded={expanded}
          onToggle={() => setExpanded((v) => !v)}
        />
      </div>
    </div>
  );
}

/**
 * Caption floats over the photo instead of stealing height from it: collapsed it shows the
 * title and the first lines, tapping expands it into a scrollable sheet (Instagram pattern).
 */
function PreviewCaption({
  lang,
  item,
  expanded,
  onToggle,
}: {
  lang: Language;
  item: PreviewItem;
  expanded: boolean;
  onToggle: () => void;
}) {
  const hasDescription = item.description.length > 0;
  return (
    <div className="pointer-events-none absolute inset-0 z-20 flex flex-col justify-end">
      <div
        className={[
          'pointer-events-auto flex min-h-0 flex-col gap-1.5 px-4 pt-10',
          // Constant bottom gap: without viewport-fit=cover the iPhone inset reads 0
          // and the home indicator would cross the button.
          'pb-[calc(env(safe-area-inset-bottom)+1.5rem)]',
          'short:pt-6 short:pb-[calc(env(safe-area-inset-bottom)+0.5rem)]',
          'transition-colors duration-300',
          expanded
            ? 'bg-black/85'
            : 'bg-gradient-to-t from-black/90 via-black/60 to-transparent',
        ].join(' ')}
      >
        {item.title ? (
          <p
            className="shrink-0 cursor-pointer truncate text-sm font-semibold"
            onClick={hasDescription ? onToggle : undefined}
          >
            {item.title}
          </p>
        ) : null}

        {hasDescription ? (
          <div
            className={[
              'min-h-0 cursor-pointer text-xs leading-relaxed text-white/80',
              // Height animates instead of snapping open; 2.25rem is exactly two lines.
              'transition-[max-height] duration-300 ease-out',
              expanded
                // touch-pan-y overrides the stage's touch-none so the sheet can scroll natively.
                ? 'max-h-[65vh] touch-pan-y overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
                : 'max-h-[2.25rem] overflow-hidden',
            ].join(' ')}
            role="button"
            tabIndex={0}
            onClick={onToggle}
            // Scrolling the open sheet must not turn the page.
            onTouchStart={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
          >
            <p className="whitespace-pre-wrap">{item.description}</p>
          </div>
        ) : null}

        {item.storefrontHref ? (
          <a
            href={item.storefrontHref}
            // On a landscape phone the button would eat the photo; the storefront stays
            // reachable from the card itself.
            className="block shrink-0 rounded-full bg-[var(--accent-primary)] px-4 py-2 text-center text-xs font-bold uppercase tracking-wide text-[var(--accent-fg)] short:hidden"
          >
            {_t('visitingCard.photoPreviewOpen', lang)}
          </a>
        ) : null}
      </div>
    </div>
  );
}

function Portrait({
  url,
  name,
  natural = false,
}: {
  url?: string | null;
  name: string;
  /** When true, height follows the image (as_is). Otherwise object-contain in the frame. */
  natural?: boolean;
}) {
  if (url) {
    return (
      <img
        src={url}
        alt=""
        className={natural ? 'block h-auto w-full' : 'h-full w-full object-contain'}
      />
    );
  }
  return (
    <div className="flex h-full min-h-[6rem] w-full items-center justify-center bg-gradient-to-br from-stone-200 to-stone-400 text-2xl font-semibold text-stone-700">
      {name.slice(0, 1).toUpperCase() || '·'}
    </div>
  );
}

function resolveBioDirection(body: string, lang: Language): 'ltr' | 'rtl' {
  const hasHe = /[\u0590-\u05FF]/.test(body);
  const hasCyr = /[\u0400-\u04FF]/.test(body);
  // Prefer script of the visible text — notes often live in one locale field while UI lang differs.
  if (hasHe && !hasCyr) return 'rtl';
  if (hasCyr && !hasHe) return 'ltr';
  return languageDirection(lang);
}

function BioBlock({ lang, card }: VisitingCardCanvasProps) {
  const title = pickI18nText(card.biography?.titleI18n, lang).trim();
  const body = pickI18nText(card.biography?.i18n, lang).trim();
  if (!card.config.showBio || (!title && !body)) return null;
  const dir = resolveBioDirection(`${title}\n${body}`, lang);
  const noteAlign = card.biography?.textAlign;
  const textAlign = noteAlign === 'center' || noteAlign === 'right' || noteAlign === 'left'
    ? noteAlign
    : (dir === 'rtl' ? 'right' : 'left');
  return (
    <div
      className="w-full"
      dir={dir}
      lang={dir === 'rtl' ? 'he' : lang}
      style={{ direction: dir, textAlign }}
    >
      {title ? (
        <h2 className="mb-2 mt-0 text-lg font-semibold leading-tight text-stone-900">{title}</h2>
      ) : null}
      {body ? (
        <SafeMarkdownContent
          markdown={body}
          allowLinks={false}
          className="w-full text-sm leading-relaxed !text-stone-700 [&_*]:!text-inherit"
        />
      ) : null}
    </div>
  );
}

function ProductTile({
  product,
  cardLayout = 'media_top',
  action = null,
  onPreview,
}: {
  product: VisitingCardProduct;
  cardLayout?: VisitingCardPayload['config']['productCardLayout'];
  action?: PhotoAction | null;
  onPreview?: () => void;
}) {
  const description = String(product.description || '').trim();

  const textBlock = (
    <div className="min-w-0 flex-1 space-y-0.5 px-1 py-1">
      <p className="truncate text-[11px] font-semibold text-stone-800">{product.name}</p>
      {description && (cardLayout !== 'media_top' || product.albumHref) ? (
        <p className="line-clamp-2 text-[10px] leading-snug text-stone-600">{description}</p>
      ) : null}
    </div>
  );

  const plainCenter = cardLayout === 'media_center_plain';
  const mediaNatural = cardLayout === 'media_center' || plainCenter;
  const mediaFrameClass =
    cardLayout === 'media_top' || cardLayout === 'media_only'
      ? 'aspect-square'
      : mediaNatural
        ? 'w-full bg-stone-200'
        : 'h-full w-[38%] shrink-0 self-stretch';

  const mediaContent = product.imageUrl ? (
    <img
      src={product.imageUrl}
      alt={cardLayout === 'media_only' ? product.name : ''}
      className={mediaNatural ? 'block h-auto w-full' : 'h-full w-full object-cover'}
    />
  ) : (
    <div
      className={`flex w-full items-center justify-center bg-stone-300 text-[10px] text-stone-500 ${
        mediaNatural ? 'min-h-[3.5rem]' : 'h-full min-h-[3.5rem]'
      }`}
    >
      ·
    </div>
  );

  /** Link only the image — text must stay tappable for flip-card reverse. */
  const media = (
    <PhotoTapFrame
      action={action}
      onPreview={onPreview}
      label={product.name}
      className={mediaFrameClass}
    >
      {mediaContent}
    </PhotoTapFrame>
  );

  let body;
  if (cardLayout === 'media_only') {
    body = media;
  } else if (cardLayout === 'media_left') {
    body = (
      <div className="flex min-h-[4.5rem] overflow-hidden">
        {media}
        {textBlock}
      </div>
    );
  } else if (cardLayout === 'media_right') {
    body = (
      <div className="flex min-h-[4.5rem] overflow-hidden">
        {textBlock}
        {media}
      </div>
    );
  } else if (cardLayout === 'media_center' || plainCenter) {
    body = (
      <div className="overflow-hidden">
        {media}
        {plainCenter ? null : (
          <div className="space-y-0.5 px-2 py-2 text-center">
            <p className="text-xs font-semibold text-stone-800">{product.name}</p>
            {description ? (
              <p className="line-clamp-3 text-[10px] leading-snug text-stone-600">{description}</p>
            ) : null}
          </div>
        )}
      </div>
    );
  } else {
    body = (
      <>
        {media}
        <p className="truncate px-1 py-1 text-[10px] font-medium text-stone-700">{product.name}</p>
      </>
    );
  }

  return (
    <div className="block overflow-hidden rounded-lg bg-stone-200 transition-opacity [&_a:hover]:opacity-90">
      {body}
    </div>
  );
}

type ProductListProps = {
  products: VisitingCardProduct[];
  cardLayout: VisitingCardPayload['config']['productCardLayout'];
  actionFor: (product: VisitingCardProduct) => PhotoAction | null;
  /** Opens the viewer on this product; swiping then walks the whole section. */
  onPreview: (product: VisitingCardProduct) => void;
};

function ProductCarousel({ products, cardLayout, actionFor, onPreview }: ProductListProps) {
  const wide = cardLayout === 'media_left' || cardLayout === 'media_right' || cardLayout === 'media_center' || cardLayout === 'media_center_plain';
  return (
    <div className="flex snap-x snap-mandatory gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {products.map((product) => (
        <div
          key={product.id}
          className={wide ? 'w-[78%] shrink-0 snap-center' : 'w-[42%] shrink-0 snap-center'}
        >
          <ProductTile
            product={product}
            cardLayout={cardLayout}
            action={actionFor(product)}
            onPreview={() => onPreview(product)}
          />
        </div>
      ))}
    </div>
  );
}

function ProductGrid({ products, cardLayout, actionFor, onPreview }: ProductListProps) {
  if (cardLayout === 'media_left' || cardLayout === 'media_right') {
    return (
      <div className="space-y-2">
        {products.map((product) => (
          <ProductTile
            key={product.id}
            product={product}
            cardLayout={cardLayout}
            action={actionFor(product)}
            onPreview={() => onPreview(product)}
          />
        ))}
      </div>
    );
  }
  if (cardLayout === 'media_center' || cardLayout === 'media_center_plain') {
    return (
      <div className="space-y-3">
        {products.map((product) => (
          <ProductTile
            key={product.id}
            product={product}
            cardLayout={cardLayout}
            action={actionFor(product)}
            onPreview={() => onPreview(product)}
          />
        ))}
      </div>
    );
  }
  return (
    <div className="grid grid-cols-3 gap-2">
      {products.map((product) => (
        <ProductTile
          key={product.id}
          product={product}
          cardLayout={cardLayout}
          action={actionFor(product)}
          onPreview={() => onPreview(product)}
        />
      ))}
    </div>
  );
}

function SectionTitle({
  title,
  lang,
  align = 'start',
}: {
  title: VisitingCardPayload['config']['worksTitle'];
  lang: Language;
  align?: VisitingCardPayload['config']['worksTitleAlign'];
}) {
  // Locale-specific only — empty for this lang means spacer (no fallback).
  const trimmed = String(title?.[lang] ?? '').trim();
  if (trimmed) {
    return (
      <h2
        className={`text-base font-semibold text-stone-900 ${
          align === 'center' ? 'text-center' : 'text-start'
        }`}
        dir={lang === 'he' ? 'rtl' : 'ltr'}
      >
        {trimmed}
      </h2>
    );
  }
  return <div className="h-2 shrink-0" aria-hidden />;
}

function isLinkedProduct(product: VisitingCardProduct): boolean {
  return product.kind === 'service_note' || Boolean(String(product.externalUrl || '').trim());
}

function filterProductsByKind(
  products: VisitingCardProduct[],
  kind: 'works' | 'services',
): VisitingCardProduct[] {
  return products.filter((p) => (kind === 'services' ? isLinkedProduct(p) : !isLinkedProduct(p)));
}

function filterGroupsByKind(
  groups: NonNullable<VisitingCardPayload['productGroups']>,
  kind: 'works' | 'services',
) {
  return groups
    .map((group) => ({
      ...group,
      products: filterProductsByKind(group.products, kind),
    }))
    .filter((group) => group.products.length > 0);
}

function productSectionHasItems(
  card: VisitingCardPayload,
  kind: 'works' | 'services',
): boolean {
  const enabled = kind === 'works' ? card.config.showCatalog : card.config.showServices;
  if (!enabled) return false;
  // Category grouping applies to works only; services stay flat in every catalog mode.
  const rawGroups =
    kind === 'works' ? card.productGroups?.filter((g) => g.products.length > 0) ?? null : null;
  if (rawGroups && rawGroups.length > 0) {
    return filterGroupsByKind(rawGroups, kind).length > 0;
  }
  return filterProductsByKind(card.products, kind).length > 0;
}

function noteAlign(value: string | undefined, fallback: 'left' | 'center' | 'right'): 'left' | 'center' | 'right' {
  return value === 'left' || value === 'right' || value === 'center' ? value : fallback;
}

function ServiceNoteText({
  markdown,
  align,
}: {
  markdown: string;
  align: 'left' | 'center' | 'right';
}) {
  if (!markdown.trim()) return null;
  return (
    <div className="min-w-0 flex-1" style={{ textAlign: align }}>
      <SafeMarkdownContent
        markdown={markdown}
        allowLinks={false}
        className="text-[11px] leading-snug !text-stone-700 [&_*]:!text-inherit"
      />
    </div>
  );
}

function ServiceNoteImages({ urls, row = false }: { urls: string[]; row?: boolean }) {
  if (urls.length === 0) return null;
  if (row) {
    return (
      <div className="flex flex-wrap gap-1">
        {urls.map((url) => (
          <img key={url} src={url} alt="" className="h-16 w-16 object-cover" />
        ))}
      </div>
    );
  }
  return (
    <div className="w-full space-y-1">
      {urls.map((url) => (
        <img key={url} src={url} alt="" className="block h-auto w-full" />
      ))}
    </div>
  );
}

/**
 * Expanded service note follows the storefront block for that layout.
 * Strip colors and paddings stay on the storefront.
 */
function ServiceNoteLayout({
  product,
  images,
  body,
  align,
}: {
  product: VisitingCardProduct;
  images: string[];
  body: string;
  align: 'left' | 'center' | 'right';
}) {
  const layout = product.noteLayout || 'stack';
  const text = <ServiceNoteText markdown={body} align={align} />;
  if (layout === 'images_row') {
    return (
      <div className="space-y-2">
        <ServiceNoteImages urls={images} row />
        {text}
      </div>
    );
  }
  if (layout === 'image_left' || layout === 'image_right') {
    return (
      <div
        className={`flex items-start gap-2 ${layout === 'image_right' ? 'flex-row-reverse' : ''}`}
        dir="ltr"
      >
        {images.length > 0 ? (
          <div className="w-[42%] shrink-0">
            <ServiceNoteImages urls={images} />
          </div>
        ) : null}
        {text}
      </div>
    );
  }
  if (layout === 'blog_alternating') {
    const alignB = noteAlign(product.textAlignB, align);
    const paragraphs = splitNoteParagraphs(body);
    const parts = paragraphs.length > 0 ? paragraphs : (body ? [body] : []);
    const imageFirstStart = product.altStart !== 'text_first';
    if (parts.length === 0) return <ServiceNoteImages urls={images} />;
    return (
      <div className="space-y-2">
        {parts.map((paragraph, index) => {
          const photo = images[index];
          const imageFirst = imageFirstStart ? index % 2 === 0 : index % 2 !== 0;
          const stripAlign = index % 2 === 0 ? align : alignB;
          return (
            <div key={index} className="space-y-1">
              {imageFirst && photo ? <img src={photo} alt="" className="block h-auto w-full" /> : null}
              <ServiceNoteText markdown={paragraph} align={stripAlign} />
              {!imageFirst && photo ? <img src={photo} alt="" className="block h-auto w-full" /> : null}
            </div>
          );
        })}
        {images.slice(parts.length).map((url) => (
          <img key={url} src={url} alt="" className="block h-auto w-full" />
        ))}
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <ServiceNoteImages urls={images} />
      {text}
    </div>
  );
}

function ServiceNoteCard({
  product,
  open,
  onToggle,
}: {
  product: VisitingCardProduct;
  open: boolean;
  onToggle: () => void;
}) {
  const align = product.textAlign === 'right' || product.textAlign === 'center' || product.textAlign === 'left'
    ? product.textAlign
    : 'left';
  const images = product.imageUrls?.length
    ? product.imageUrls
    : (product.imageUrl ? [product.imageUrl] : []);
  const cover = images[0] || null;
  const title = String(product.name || '').trim();
  const body = String(product.description || '').trim();
  return (
    <article className="overflow-hidden rounded-lg bg-stone-200">
      <button
        type="button"
        className="block w-full"
        data-no-flip
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        onPointerDown={(e) => e.stopPropagation()}
      >
        {!open && cover ? (
          <img src={cover} alt="" className="block h-auto w-full" />
        ) : null}
        <span className="flex items-center gap-2 px-2 py-2">
          <span className="min-w-0 flex-1 text-xs font-semibold text-stone-800" style={{ textAlign: align }}>
            {title}
          </span>
          <span
            aria-hidden
            className={`inline-block h-1.5 w-1.5 shrink-0 border-b-2 border-r-2 border-stone-500 ${
              open ? '-translate-y-0.5 rotate-[-135deg]' : 'translate-y-[-1px] rotate-45'
            }`}
          />
        </span>
      </button>
      {open && (images.length > 0 || body) ? (
        <div className="px-2 pb-2">
          <ServiceNoteLayout product={product} images={images} body={body} align={align} />
        </div>
      ) : null}
    </article>
  );
}

function ProductSection({
  lang,
  card,
  kind,
}: {
  lang: Language;
  card: VisitingCardPayload;
  kind: 'works' | 'services';
}) {
  const preview = useContext(PreviewContext);
  const [openNoteId, setOpenNoteId] = useState<string | null>(null);
  const enabled = kind === 'works' ? card.config.showCatalog : card.config.showServices;

  const rawGroups =
    kind === 'works' ? card.productGroups?.filter((g) => g.products.length > 0) ?? null : null;
  const groups = rawGroups && rawGroups.length > 0 ? filterGroupsByKind(rawGroups, kind) : null;
  const products = groups
    ? groups.flatMap((group) => group.products)
    : filterProductsByKind(card.products, kind);

  // Swiping in the viewer walks the whole section, across category groups too.
  const previewItems = previewItemsFrom(card, lang, products);

  const actionFor = (product: VisitingCardProduct) => buildPhotoAction(card, lang, product);
  const onPreview = (product: VisitingCardProduct) => {
    if (!preview) return;
    if (kind === 'works') {
      // Card grid stays capped; the viewer walks the whole listed catalog.
      preview.openCatalogAt(product.id, previewItems);
      return;
    }
    const index = previewItems.findIndex((entry) => entry.productId === product.id);
    if (index >= 0) preview.open(previewItems, index);
  };

  if (!enabled) return null;

  const title = kind === 'works' ? card.config.worksTitle : card.config.servicesTitle;
  const titleAlign = kind === 'works'
    ? card.config.worksTitleAlign
    : card.config.servicesTitleAlign;
  const cardLayout = card.config.productCardLayout || 'media_top';
  const useCarousel =
    (kind === 'services' || card.config.catalogMode !== 'by_category')
    && card.config.catalogPresentation === 'carousel';

  if (groups) {
    if (groups.length === 0) return null;
    return (
      <div className="space-y-4">
        <SectionTitle title={title} lang={lang} align={titleAlign} />
        {groups.map((group) => (
          <div key={group.id} className="space-y-2">
            <h3 className="text-xs font-bold uppercase tracking-wide text-stone-500">{group.name}</h3>
            <ProductGrid products={group.products} cardLayout={cardLayout} actionFor={actionFor} onPreview={onPreview} />
          </div>
        ))}
      </div>
    );
  }

  if (products.length === 0) return null;

  if (products.every((product) => product.kind === 'service_note')) {
    return (
      <div className="space-y-4">
        <SectionTitle title={title} lang={lang} align={titleAlign} />
        {products.map((product, index) => (
          <div key={product.id}>
            {index > 0 ? (
              <div
                aria-hidden
                className="mb-4 text-center text-sm font-black leading-none text-stone-500"
              >
                ●
              </div>
            ) : null}
            <ServiceNoteCard
              product={product}
              open={openNoteId === product.id}
              onToggle={() => {
                setOpenNoteId((current) => (current === product.id ? null : product.id));
              }}
            />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <SectionTitle title={title} lang={lang} align={titleAlign} />
      {useCarousel ? (
        <ProductCarousel products={products} cardLayout={cardLayout} actionFor={actionFor} onPreview={onPreview} />
      ) : (
        <ProductGrid products={products} cardLayout={cardLayout} actionFor={actionFor} onPreview={onPreview} />
      )}
    </div>
  );
}

function CatalogBlock({ lang, card }: VisitingCardCanvasProps) {
  return (
    <>
      {visitingCardCatalogKindsInOrder(card.config).map((kind) => (
        <ProductSection key={kind} lang={lang} card={card} kind={kind} />
      ))}
    </>
  );
}

function ContactsBlock({
  lang,
  card,
  portraitUrl,
}: VisitingCardCanvasProps & { portraitUrl?: string | null }) {
  return (
    <VisitingCardContactButtons
      lang={lang}
      template={card.config.contactButtons}
      contacts={card.contacts}
      tenantSlug={card.tenant.slug}
      showStorefrontLink={card.config.showStorefrontLink}
      storefrontHref={card.catalogShowcaseUrl === undefined ? undefined : card.catalogShowcaseUrl}
      hiddenContactIds={card.config.hiddenContactIds}
      portraitUrl={portraitUrl}
      displayName={card.tenant.name}
    />
  );
}

/**
 * Hero photo + contacts under it — except `dock` (sticky footer) and
 * `orbit` (photo + buttons are one composed block).
 */
function PhotoWithContacts({ lang, card }: VisitingCardCanvasProps) {
  const preview = useContext(PreviewContext);
  const portrait = card.biography?.photoUrls?.[0] || card.tenant.logoUrl || null;
  const template = card.config.contactButtons;
  const mode = effectiveVisitingCardPhotoLink(card.config);
  const slug = card.tenant.slug;

  // In preview mode the hero opens the BIO photo set; otherwise it can only lead to the storefront.
  const bioPhotos = (card.biography?.photoUrls || []).filter(Boolean);
  const heroItems: PreviewItem[] = (bioPhotos.length > 0 ? bioPhotos : [portrait].filter(Boolean))
    .map((url, i) => ({
      id: `portrait-${i}`,
      productId: 'portrait',
      imageUrl: String(url),
      title: card.tenant.name,
      description: '',
      storefrontHref: card.config.cardOnly || !slug ? null : tenantCatalogUrl(slug, lang),
    }));

  const heroAction: PhotoAction | null =
    mode === 'none' || !slug
      ? null
      : mode === 'preview'
        ? (heroItems.length > 0 ? { kind: 'preview' } : null)
        : { kind: 'href', href: tenantCatalogUrl(slug, lang), external: false };
  const isOrbit = template === 'orbit';
  const isDock = template === 'dock';
  const heroAspect = card.config.heroPhotoAspect || '4_3';
  const heroFrameClass =
    heroAspect === 'as_is'
      ? 'w-full rounded-lg bg-stone-200'
      : [
          'w-full rounded-lg bg-stone-200',
          heroAspect === '1_1'
            ? 'aspect-square'
            : heroAspect === '2_3'
              ? 'aspect-[2/3]'
              : heroAspect === '4_5'
                ? 'aspect-[4/5]'
                : 'aspect-[4/3]',
        ].join(' ');

  if (isOrbit) {
    return (
      <div className="flex justify-center py-2">
        <ContactsBlock lang={lang} card={card} portraitUrl={portrait} />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <PhotoTapFrame
        action={heroAction}
        onPreview={() => preview?.open(heroItems, 0)}
        label={card.tenant.name}
        className={heroFrameClass}
      >
        <Portrait
          url={portrait}
          name={card.tenant.name}
          natural={heroAspect === 'as_is'}
        />
      </PhotoTapFrame>
      {!isDock ? <ContactsBlock lang={lang} card={card} portraitUrl={portrait} /> : null}
    </div>
  );
}

function StickyDock({ lang, card }: VisitingCardCanvasProps) {
  if (card.config.contactButtons !== 'dock') return null;
  const portrait = card.biography?.photoUrls?.[0] || card.tenant.logoUrl || null;
  // Bleed into PhoneFrame bottom safe-area so accent fills screen corners.
  return (
    <div className="shrink-0 -mb-[env(safe-area-inset-bottom)]">
      <ContactsBlock lang={lang} card={card} portraitUrl={portrait} />
    </div>
  );
}

function SheetLayout({ lang, card }: VisitingCardCanvasProps) {
  const dock = card.config.contactButtons === 'dock';
  return (
    <div className="flex h-full flex-col">
      <div className="visiting-card-scroll min-h-0 flex-1 space-y-4 overflow-y-auto px-4 pb-4 pt-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <PhotoWithContacts lang={lang} card={card} />
        <BioBlock lang={lang} card={card} />
        <PublishedReviews lang={lang} />
        <div className="space-y-4">
          <CatalogBlock lang={lang} card={card} />
        </div>
      </div>
      {dock ? <StickyDock lang={lang} card={card} /> : null}
    </div>
  );
}

/**
 * Flip panels: contacts/bio first, then one panel per catalog section in configured order.
 * Only two physical faces exist, so the hidden face receives the next panel before each turn.
 */
function useFlipPanels({ lang, card }: VisitingCardCanvasProps) {
  return useMemo(() => {
    const panels: { key: string; label: string; node: ReactNode }[] = [
      {
        key: 'front',
        label: _t('visitingCard.flipFaceContacts', lang),
        node: (
          <>
            <PhotoWithContacts lang={lang} card={card} />
            <BioBlock lang={lang} card={card} />
            <PublishedReviews lang={lang} />
          </>
        ),
      },
    ];

    for (const kind of visitingCardCatalogKindsInOrder(card.config)) {
      if (!productSectionHasItems(card, kind)) continue;
      const title = kind === 'works' ? card.config.worksTitle : card.config.servicesTitle;
      panels.push({
        key: kind,
        label:
          pickI18nText(title, lang).trim()
          || _t(kind === 'works' ? 'visitingCard.flipFaceWorks' : 'visitingCard.flipFaceServices', lang),
        node: (
          <>
            <ProductSection lang={lang} card={card} kind={kind} />
          </>
        ),
      });
    }

    return panels;
  }, [lang, card]);
}

function FlipLayout({ lang, card }: VisitingCardCanvasProps) {
  const panels = useFlipPanels({ lang, card });
  const [step, setStep] = useState(0);
  /** Signed turn count: spin direction always matches the direction the user asked for. */
  const [turns, setTurns] = useState(0);
  const [faces, setFaces] = useState({ front: 0, back: 1 });
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const dock = card.config.contactButtons === 'dock';
  const showingBack = Math.abs(turns % 2) === 1;

  // Panel count changes with settings (live preview) — never leave a turned card on a gone panel.
  useEffect(() => {
    if (step < panels.length) return;
    setStep(0);
    setTurns(0);
    setFaces({ front: 0, back: Math.min(1, Math.max(0, panels.length - 1)) });
  }, [panels.length, step]);

  const go = (dir: 1 | -1) => {
    if (panels.length < 2) return;
    const next = (step + dir + panels.length) % panels.length;
    setStep(next);
    // Load the incoming panel onto the face that is currently hidden.
    setFaces((prev) => (showingBack ? { ...prev, front: next } : { ...prev, back: next }));
    setTurns((v) => v + dir);
  };

  const onTouchStart = (e: TouchEvent) => {
    const t = e.changedTouches[0];
    touchStart.current = t ? { x: t.clientX, y: t.clientY } : null;
  };
  const onTouchEnd = (e: TouchEvent) => {
    const start = touchStart.current;
    touchStart.current = null;
    if (start == null) return;
    const target = e.target as HTMLElement | null;
    if (target?.closest('a, button, input, textarea, select, [data-no-flip]')) return;
    const end = e.changedTouches[0];
    if (!end) return;
    const deltaX = end.clientX - start.x;
    const deltaY = end.clientY - start.y;
    // Prefer vertical scroll over flip when the gesture is mostly up/down.
    if (Math.abs(deltaX) < 48 || Math.abs(deltaX) < Math.abs(deltaY)) return;
    go(deltaX < 0 ? 1 : -1);
  };

  /** Tap on the left half turns back, right half turns forward — the card follows the hand. */
  const onCardClick = (e: MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement | null;
    if (target?.closest('a, button, input, textarea, select, [data-no-flip]')) return;
    const rect = e.currentTarget.getBoundingClientRect();
    go(e.clientX - rect.left < rect.width / 2 ? -1 : 1);
  };

  const nextLabel = panels.length > 1 ? panels[(step + 1) % panels.length].label : '';

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col px-4 pt-3">
        <div className="mb-2 flex items-center justify-end gap-2">
          {panels.length > 1 ? (
            <button
              type="button"
              onClick={() => go(1)}
              className="shrink-0 rounded-full bg-[var(--accent-primary)] px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-[var(--accent-fg)]"
            >
              {nextLabel}
            </button>
          ) : null}
        </div>
        <div
          className="relative min-h-0 flex-1"
          style={{ perspective: '1200px' }}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <div
            role="button"
            tabIndex={0}
            aria-label={nextLabel || panels[step]?.label || ''}
            onClick={onCardClick}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowRight') {
                e.preventDefault();
                go(1);
              } else if (e.key === 'ArrowLeft') {
                e.preventDefault();
                go(-1);
              }
            }}
            className="relative h-full w-full cursor-pointer outline-none"
            style={{
              transformStyle: 'preserve-3d',
              transform: `rotateY(${turns * 180}deg)`,
              transition: 'transform 500ms ease',
            }}
          >
            <div
              className="visiting-card-scroll absolute inset-0 space-y-3 overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              style={{
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden',
                // Mobile hit-testing ignores backfaceVisibility — only the visible face must receive touches.
                pointerEvents: showingBack ? 'none' : 'auto',
              }}
            >
              {panels[faces.front]?.node ?? null}
            </div>
            <div
              className="visiting-card-scroll absolute inset-0 space-y-4 overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              style={{
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden',
                transform: 'rotateY(180deg)',
                pointerEvents: showingBack ? 'auto' : 'none',
              }}
            >
              {panels[faces.back]?.node ?? null}
            </div>
          </div>
        </div>
      </div>
      {dock ? <StickyDock lang={lang} card={card} /> : null}
    </div>
  );
}

function SliderLayout({ lang, card }: VisitingCardCanvasProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  const dock = card.config.contactButtons === 'dock';

  const catalogSlides = visitingCardCatalogKindsInOrder(card.config)
    .filter((kind) => productSectionHasItems(card, kind))
    .map((kind) => ({
      key: kind,
      node: (
        <div className="visiting-card-scroll flex h-full flex-col gap-3 overflow-y-auto p-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <ProductSection lang={lang} card={card} kind={kind} />
        </div>
      ),
    }));

  const slides = [
    {
      key: 'photo',
      node: (
        <div className="visiting-card-scroll flex h-full flex-col gap-3 overflow-y-auto p-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <PhotoWithContacts lang={lang} card={card} />
          <PublishedReviews lang={lang} />
        </div>
      ),
    },
    {
      key: 'bio',
      node: (
        <div className="visiting-card-scroll flex h-full flex-col gap-4 overflow-y-auto p-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <BioBlock lang={lang} card={card} />
        </div>
      ),
    },
    ...catalogSlides,
  ].filter((slide) => {
    if (slide.key === 'bio' && !card.config.showBio) return false;
    return true;
  });

  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const next = Math.round(el.scrollLeft / Math.max(el.clientWidth, 1));
    setIndex(Math.min(slides.length - 1, Math.max(0, next)));
  };

  const goTo = (i: number) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTo({ left: i * el.clientWidth, behavior: 'smooth' });
    setIndex(i);
  };

  return (
    <div className="relative flex h-full flex-col">
      <div
        ref={scrollerRef}
        onScroll={onScroll}
        className="flex min-h-0 flex-1 snap-x snap-mandatory overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {slides.map((slide) => (
          <section
            key={slide.key}
            className="relative h-full w-full shrink-0 snap-center overflow-hidden"
          >
            {slide.node}
          </section>
        ))}
      </div>
      {slides.length > 1 ? (
        <div className="flex items-center justify-center gap-2 py-2">
          {slides.map((slide, i) => (
            <button
              key={slide.key}
              type="button"
              aria-label={slide.key}
              onClick={() => goTo(i)}
              className={[
                'h-1.5 rounded-full transition-all',
                i === index ? 'w-5 bg-[var(--accent-primary)]' : 'w-1.5 bg-stone-300',
              ].join(' ')}
            />
          ))}
        </div>
      ) : null}
      {dock ? <StickyDock lang={lang} card={card} /> : null}
    </div>
  );
}

export function VisitingCardCanvas({
  lang,
  card,
  onPreviewOpenChange,
}: VisitingCardCanvasRootProps) {
  const layout = card.config.layout;
  const [preview, setPreview] = useState<{ items: PreviewItem[]; index: number } | null>(null);
  const galleryCacheRef = useRef<{ lang: Language; items: PreviewItem[] } | null>(null);
  const galleryInflightRef = useRef<Promise<PreviewItem[]> | null>(null);

  const open = useCallback((items: PreviewItem[], index: number) => {
    if (items.length > 0) setPreview({ items, index });
  }, []);

  const closePreview = useCallback(() => setPreview(null), []);

  const mapGallerySlides = useCallback(
    (slides: Array<{
      id: string;
      productId: string;
      imageUrl: string;
      title: string;
      description: string;
    }>): PreviewItem[] => {
      const slug = card.tenant.slug;
      return slides.map((slide) => ({
        ...slide,
        storefrontHref: slug
          ? tenantCatalogProductUrl(slug, slide.productId, lang)
          : null,
      }));
    },
    [card.tenant.slug, lang],
  );

  const loadCatalogGallery = useCallback(async (): Promise<PreviewItem[]> => {
    const cached = galleryCacheRef.current;
    if (cached && cached.lang === lang) return cached.items;
    if (galleryInflightRef.current) return galleryInflightRef.current;

    const request = api
      .getPublicVisitingCardGallery(lang)
      .then((res) => {
        const items = mapGallerySlides(res.slides || []);
        galleryCacheRef.current = { lang, items };
        return items;
      })
      .finally(() => {
        galleryInflightRef.current = null;
      });
    galleryInflightRef.current = request;
    return request;
  }, [lang, mapGallerySlides]);

  // Drop the cached filmstrip when the card payload changes (live socket reload).
  useEffect(() => {
    galleryCacheRef.current = null;
  }, [card]);

  const openCatalogAt = useCallback(
    (productId: string, fallback: PreviewItem[]) => {
      const localIndex = fallback.findIndex((entry) => entry.productId === productId);
      if (fallback.length > 0 && localIndex >= 0) {
        setPreview({ items: fallback, index: localIndex });
      }

      void loadCatalogGallery()
        .then((items) => {
          if (!items.length) return;
          setPreview((prev) => {
            const currentId = prev?.items[prev.index]?.id;
            const byId = currentId ? items.findIndex((entry) => entry.id === currentId) : -1;
            if (byId >= 0) return { items, index: byId };
            const byProduct = items.findIndex((entry) => entry.productId === productId);
            return { items, index: byProduct >= 0 ? byProduct : 0 };
          });
        })
        .catch(() => {
          // Keep the curated slice already on screen — empty would strand the visitor.
        });
    },
    [loadCatalogGallery],
  );

  const previewApi = useMemo<PreviewApi>(
    () => ({ open, openCatalogAt }),
    [open, openCatalogAt],
  );

  useEffect(() => {
    onPreviewOpenChange?.(preview != null);
  }, [preview, onPreviewOpenChange]);

  return (
    <VisitingCardNewsProvider
      lang={lang}
      tenantSlug={card.tenant.slug}
      dockOffset={card.config.contactButtons === 'dock'}
    >
      <PreviewContext.Provider value={previewApi}>
        <div className="relative flex h-full w-full flex-col overflow-hidden bg-[#f4f1ec]">
          <div className="min-h-0 flex-1">
            {layout === 'flip' ? (
              <FlipLayout lang={lang} card={card} />
            ) : layout === 'slider' ? (
              <SliderLayout lang={lang} card={card} />
            ) : (
              <SheetLayout lang={lang} card={card} />
            )}
          </div>
          <div className="shrink-0 px-4">
            <VisitingCardA11yFooter lang={lang} tenantSlug={card.tenant.slug} />
          </div>
          {preview ? (
            <PhotoPreview
              lang={lang}
              items={preview.items}
              index={preview.index}
              onIndex={(index) => setPreview((prev) => (prev ? { ...prev, index } : prev))}
              onClose={closePreview}
            />
          ) : null}
        </div>
      </PreviewContext.Provider>
    </VisitingCardNewsProvider>
  );
}
